import React from "react";
import { FileText, Scale, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#002FA7] mb-8" data-testid="tos-back-link">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="bg-white border-2 border-black p-8 sm:p-12" data-testid="terms-of-service-page">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center">
              <Scale size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Terms of Service</h1>
              <p className="text-sm text-gray-500 mt-1">Last updated: February 1, 2026</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">1. Acceptance of Terms</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                By accessing or using the MOSAIC platform ("Service"), operated by Lerno Co. ("we", "us", "our"), you agree to be bound by these Terms of Service ("Terms"). If you are using the Service on behalf of a school, district, or organization, you represent that you have authority to bind that entity to these Terms. If you do not agree, do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">2. Description of Service</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                MOSAIC is a WIDA-aligned English language proficiency assessment platform designed for K-12 schools in New York State. The platform provides assessment creation, administration, scoring, growth tracking, and reporting across four language modalities: Listening, Speaking, Reading, and Writing.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">3. User Accounts &amp; Roles</h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                Access to the Service requires an authenticated account. Accounts are provisioned by authorized school or district administrators. The Service implements five role tiers:
              </p>
              <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                <li><strong>Student</strong> — Takes assessments, views feedback and progress</li>
                <li><strong>Teacher</strong> — Manages classes, grades assessments, tracks student growth</li>
                <li><strong>School Admin</strong> — Oversees school-level operations and user management</li>
                <li><strong>District Admin</strong> — Manages district-wide assignments and analytics</li>
                <li><strong>Super Admin</strong> — Platform-wide administration, licensing, compliance</li>
              </ul>
              <p className="text-sm text-gray-700 leading-relaxed mt-3">
                You are responsible for maintaining the confidentiality of your account credentials. Notify your administrator immediately of any unauthorized use.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">4. Licensing &amp; Permitted Use</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Schools access MOSAIC through a license agreement that specifies the grade bands covered (e.g., K-5, K-8, 6-8, 9-12, or All Grades). Use is restricted to the licensed grade levels. The Service may only be used for legitimate educational assessment purposes. Any use for commercial data mining, advertising, or non-educational profiling is strictly prohibited.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">5. Data Privacy</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Our collection and use of personal information is governed by our <Link to="/privacy" className="text-[#002FA7] underline">Privacy Policy</Link>, which is incorporated into these Terms by reference. We comply with FERPA, COPPA, and NYS Education Law &sect; 2-d. Schools retain ownership of all student data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">6. Intellectual Property</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                The MOSAIC platform, including its design, code, content, and WIDA-aligned assessment questions, is the intellectual property of Lerno Co. Schools and districts retain full ownership of their student data, assessment results, and any content they upload.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">7. Acceptable Use</h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                <li>Share assessment content with unauthorized parties</li>
                <li>Attempt to circumvent exam integrity controls</li>
                <li>Use the Service for any purpose other than educational assessment</li>
                <li>Reverse engineer, decompile, or attempt to extract source code</li>
                <li>Upload malicious content or attempt to disrupt the Service</li>
                <li>Access data belonging to other schools or districts</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">8. Service Availability</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                We strive to maintain high availability but do not guarantee uninterrupted access. Scheduled maintenance will be communicated in advance. We are not liable for disruptions caused by factors beyond our reasonable control.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">9. Termination</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Either party may terminate the agreement with 30 days' written notice. Upon termination, we will provide a data export and securely delete all school/district data within 90 days, unless retention is required by law. Termination does not affect obligations that accrued prior to termination.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">10. Limitation of Liability</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                To the maximum extent permitted by law, Lerno Co.'s total liability under these Terms shall not exceed the fees paid by the school or district in the 12 months preceding the claim. We are not liable for indirect, incidental, special, or consequential damages.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">11. Governing Law</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                These Terms are governed by the laws of the State of New York, without regard to conflict of law principles. Any disputes shall be resolved in the courts of New York County, New York.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold border-b-2 border-gray-200 pb-2 mb-4">12. Contact</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                For questions about these Terms:<br />
                <strong>Lerno Co.</strong><br />
                Email: legal@mosaicassessmentco.com<br />
                Web: <a href="https://mosaicassessmentco.com" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline">mosaicassessmentco.com</a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
